/**
 * Работы фирмы: страница берётся в форме контракта, обход идёт по курсору.
 *
 * Обход **заканчивается**: курсор следующей страницы существует только вместе
 * с признаком «есть ещё». Непустой курсор на последней странице — приманка, на
 * которой обход зацикливался, и ловил её ручной прогон, а не тесты.
 *
 * ПЕРЕВОРОТ блока `WEB-2.2` (спецификация §6, строка «works.ts»; требования 211
 * и 406). Было: экран без фирмы, лента всех работ смотрящего, строка —
 * машинные значения. Стало: экран получает фирму из адреса и зовёт ленту с
 * `company_id` на **каждой** странице, а в режиме «ждут назначения» — ещё с
 * `needs_assignment=true`. Отбор делает сервер (серверное требование 731):
 * клиент не сравнивает с фирмой ни поле работы, ни сторону, ни рынок, ни
 * статус — отбор страницы, пришедшей курсором, вдобавок дал бы дыры. Что
 * прежний ловил, а новый не ловит: ничего — обход, порядок сервера, одно
 * состояние отказа и возврат на вход те же.
 *
 * Строка — имя работы (`title` сервера), статус подписью строителя
 * (значение вне перечня — «неизвестно», CR-007), идентификатор работы и, при
 * привязке, тягач видом и VIN и имя водителя. «Назначить» и «Заменить» стоят
 * ровно при действии сервера: элемент `available_actions` со статусом
 * назначения (решение формы Б12). Это значение статуса — единственное,
 * названное в файле; создания и рынка здесь нет — «ждёт назначения» считает
 * сервер, а не клиент по статусу.
 *
 * ПЕРЕВОРОТ блока `WEB-3.1` (спецификация §6). Было: строка — текст, переход
 * только на назначение. Стало: имя работы — ссылка на карточку; ссылка
 * назначения остаётся ровно при действии сервера. Что прежний ловил, а новый не
 * ловит: ничего.
 *
 * ПЕРЕВОРОТ блока `WEB-D.1`, решения `Б4`, `Б5`, `Б6`. Было: строка `li` с текстом и
 * id работы. Стало: `tr.work-row` таблицы на восемь столбцов, id в строке нет, он в
 * карточке; пусто — прочерк. До 768 px та же разметка становится карточками
 * стилями, подпись даёт `data-label`. Запрос, порядок и условие действия прежние.
 *
 * Отказ посреди обхода не стирает полученного: страницы уже пришли и уже
 * верны, а курсор остаётся — повторная дозагрузка продолжает с того же места.
 *
 * Отвергнутый токен — не отказ, а другой исход: экран отказа вместо экрана
 * входа выглядит поломкой сервера. Различаются они машинным кодом.
 */

import { ApiFailure, UNAUTHENTICATED_CODE } from '../api/client';
import type { ApiClient } from '../api/client';
import { nextPageCursor } from '../api/contract';
import type { CompanySection, Work } from '../api/contract';
import { activeLocale, vehicleKindStringKey, workStatusStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { internalWorkNewPath, workAssignmentPath, workCardPath } from '../router';
import { dateLabel, enumLabel, moneyLabel, placeState, workInternalMark, workStatusPill } from './state';

export interface WorksScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  /** Фирма из адреса: работы, где она сторона, отбирает сервер. */
  readonly companyId: string;
  /** Режим «ждут назначения» — отбор сервера признаком `needs_assignment`. */
  readonly needsAssignment?: boolean;
  /** Разделы фирмы от сервера: кнопка внутреннего заказа следует за ними (`WEB-3.5`, `Б1`). */
  readonly sections?: readonly CompanySection[];
  readonly onUnauthenticated: () => void;
}

export interface WorksScreen {
  readonly element: HTMLElement;
  readonly works: readonly Work[];
  readonly hasMore: boolean;
  readonly failed: boolean;
  /** Обход с начала: первая страница запрашивается без курсора. */
  load(): Promise<void>;
  /** Следующая страница по курсору предыдущей. Без курсора — ничего. */
  loadMore(): Promise<void>;
}

/**
 * Статус действия, которым сервер разрешает назначение и замену, —
 * **единственное** значение статуса, названное в этом файле. Оно решает не
 * «можно ли», а «ставить ли ссылку»: назначение всё равно судит guard сервера.
 */
const ASSIGN_ACTION: Work['status'] = 'ASSIGNED';

/**
 * Раздел, при котором стоит кнопка внутреннего заказа (спецификация `WEB-3.5` §2):
 * клиент право не вычисляет, остальное судит отказ сервера.
 */
const INTERNAL_WORK_SECTION: CompanySection = 'works_to_assign';

/** Значения нет. */
const NO_VALUE = '—';

export function createWorksScreen(options: WorksScreenOptions): WorksScreen {
  const { client, strings, companyId, needsAssignment = false, sections = [], onUnauthenticated } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const box = document.createElement('div');
  const table = document.createElement('table');
  const tableHead = document.createElement('thead');
  const headRow = document.createElement('tr');
  const tableBody = document.createElement('tbody');
  const notice = document.createElement('p');
  const more = document.createElement('button');

  const frame: HTMLElement[] = [heading];

  if (!needsAssignment && sections.includes(INTERNAL_WORK_SECTION)) {
    const create = document.createElement('a');

    create.className = 'action action--primary';
    create.href = internalWorkNewPath(companyId);
    create.textContent = strings.works_internal_new;
    frame.push(create);
  }

  element.className = 'screen screen-works';
  heading.textContent = needsAssignment ? strings.works_to_assign_title : strings.works_title;
  notice.className = 'notice';
  notice.textContent = strings.works_failed;
  more.textContent = strings.works_more;
  more.type = 'button';
  more.className = 'action';
  box.className = 'data-table-box';
  table.className = 'data-table';
  // Роли явно: карточный вид меняет `display`, и браузер снимает табличные роли.
  table.setAttribute('role', 'table');
  tableHead.setAttribute('role', 'rowgroup');
  tableBody.setAttribute('role', 'rowgroup');
  headRow.setAttribute('role', 'row');

  const columns = {
    status: strings.works_column_status,
    route: strings.works_column_route,
    cargo: strings.works_column_cargo,
    loading: strings.works_column_loading,
    vehicle: strings.works_column_vehicle,
    driver: strings.works_column_driver,
    price: strings.work_card_price,
    action: strings.works_column_action,
  };

  for (const caption of Object.values(columns)) {
    const header = document.createElement('th');

    header.scope = 'col';
    header.setAttribute('role', 'columnheader');
    header.textContent = caption;
    headRow.append(header);
  }

  tableHead.append(headRow);
  table.append(tableHead, tableBody);
  box.append(table);

  let works: readonly Work[] = [];
  let cursor: string | null = null;
  let hasMore = false;
  let failed = false;
  /** Первая страница ещё не ответила — на экране «загрузка». */
  let pending = true;

  /** Действие назначения, которое сервер отдал работе, — есть ли оно. */
  function offersAssignment(item: Work): boolean {
    for (const action of item.available_actions) {
      if (action.status === ASSIGN_ACTION) {
        return true;
      }
    }

    return false;
  }

  function cell(caption: string, content: string | HTMLElement | null, modifier?: string): HTMLTableCellElement {
    const node = document.createElement('td');

    node.dataset.label = caption;
    node.setAttribute('role', 'cell');

    if (modifier !== undefined) {
      node.className = modifier;
    }

    if (content !== null) {
      node.append(content);
    }

    return node;
  }

  function row(item: Work): HTMLElement {
    const node = document.createElement('tr');
    const open = document.createElement('a');
    const { cargo, assignment, price } = item;

    node.className = 'work-row';
    node.setAttribute('role', 'row');

    // Имя работы ведёт на её карточку (блок `WEB-3.1`, требование 009).
    open.className = 'company-link';
    open.href = workCardPath(companyId, item.id);
    open.textContent = item.title;

    let action: HTMLElement | null = null;

    if (offersAssignment(item)) {
      const link = document.createElement('a');

      link.className = 'action action--text';
      link.href = workAssignmentPath(companyId, item.id);
      link.textContent = assignment === null ? strings.works_assign : strings.works_replace;
      action = link;
    }

    const route = cell(columns.route, open, 'data-table__route');

    if (item.internal) {
      route.append(workInternalMark(strings));
    }

    node.append(
      cell(columns.status, workStatusPill(strings, workStatusStringKey(item.status))),
      route,
      cell(columns.cargo, cargo?.cargo_type ?? NO_VALUE),
      cell(columns.loading, cargo == null ? NO_VALUE : dateLabel(activeLocale(), cargo.loading_at), 'data-table__mono'),
      cell(
        columns.vehicle,
        assignment === null
          ? NO_VALUE
          : `${enumLabel(strings, vehicleKindStringKey(assignment.tractor.kind))} ${assignment.tractor.vin}`,
        'data-table__mono',
      ),
      cell(columns.driver, assignment === null ? NO_VALUE : (assignment.driver.name ?? strings.work_assignment_unnamed)),
      cell(columns.price, price == null ? NO_VALUE : moneyLabel(strings, price), 'data-table__mono'),
      cell(columns.action, action, 'data-table__action'),
    );

    return node;
  }

  function render(): void {
    const rows: HTMLElement[] = [];

    for (const item of works) {
      rows.push(row(item));
    }

    tableBody.replaceChildren(...rows);

    if (pending) {
      placeState(element, { state: 'loading', answered: false, frame, list: box }, strings);

      return;
    }

    const after: HTMLElement[] = [];

    if (failed) {
      after.push(notice);
    }

    if (hasMore) {
      after.push(more);
    }

    // Пустой ответ — ответ пришедший: подпись встаёт на место списка, заголовок
    // остаётся. Отказ — текстом под полученным: страницы уже верны.
    placeState(
      element,
      {
        state: !failed && works.length === 0 ? 'empty' : null,
        answered: true,
        frame,
        list: box,
        after,
        emptyCaption: needsAssignment ? strings.works_to_assign_empty : strings.works_empty,
      },
      strings,
    );
  }

  async function loadPage(from: string | null): Promise<void> {
    // Фирма — на каждой странице, признак «ждут назначения» — только в своём
    // режиме: незаданный параметр не уходит вовсе.
    const filter = needsAssignment ? { company_id: companyId, needs_assignment: true } : { company_id: companyId };

    try {
      const page = await client.listWorks(from === null ? filter : { ...filter, cursor: from });

      works = [...works, ...page.data];
      cursor = nextPageCursor(page.meta);
      hasMore = cursor !== null;
      failed = false;
    } catch (error) {
      if (error instanceof ApiFailure && error.code === UNAUTHENTICATED_CODE) {
        // Возврат на вход и состояние отказа взаимоисключающи всегда, а не
        // только из чистого состояния: вкладка уходит на вход, и отказ ленты
        // после этого не относится ни к чему. Полученные страницы при этом
        // остаются — они уже пришли и уже верны.
        failed = false;
        onUnauthenticated();
      } else {
        // Полученное остаётся на экране, курсор не теряется: сбой следующей
        // страницы не делает предыдущие неверными.
        failed = true;
      }
    }

    pending = false;
    render();
  }

  async function load(): Promise<void> {
    works = [];
    cursor = null;
    hasMore = false;
    failed = false;
    pending = true;
    render();

    await loadPage(null);
  }

  async function loadMore(): Promise<void> {
    if (!hasMore || cursor === null) {
      return;
    }

    await loadPage(cursor);
  }

  more.addEventListener('click', () => {
    void loadMore();
  });

  render();

  return {
    element,

    get works(): readonly Work[] {
      return works;
    },

    get hasMore(): boolean {
      return hasMore;
    },

    get failed(): boolean {
      return failed;
    },

    load,
    loadMore,
  };
}
